import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import firebase from 'firebase/app';
import 'firebase/functions';

export interface MoveJob {
  studyInstanceUid: string;
  jobId?: string;
  status?: string;
  progress?: number;
  error?: string;
  targetFacilityName?: string;
  expiresAt?: string;
}

export interface MoveStudiesResponse {
  facilityId: string;
  destinationModality: string;
  jobs: MoveJob[];
}

export interface MoveJobStatus {
  jobId: string;
  facilityId: string;
  status: string;
  progress: number;
  error?: string;
  description?: string;
  timestamp?: string;
}

export interface MoveStudiesRequest {
  studyInstanceUids: string[];
  targetModality: string;
}

export interface Modality {
  name: string;
  aet?: string;
  host?: string;
  port?: number;
}

export interface ModalitiesResponse {
  modalities: Modality[];
}

@Injectable({ providedIn: 'root' })
export class MoveService {
  private readonly baseUrl = environment.bffBaseUrl;

  constructor(private http: HttpClient) {}

  /**
   * Gets the list of available modalities configured in Orthanc
   */
  getModalities(): Observable<ModalitiesResponse> {
    return this.http.get<ModalitiesResponse>(`${this.baseUrl}/modalities`);
  }

  /**
   * Initiates C-MOVE operation for one or more studies to a target modality
   */
  moveStudies(studyInstanceUids: string[], targetModality: string): Observable<MoveStudiesResponse> {
    const body: MoveStudiesRequest = { studyInstanceUids, targetModality };
    return this.http.post<MoveStudiesResponse>(`${this.baseUrl}/move/studies`, body);
  }

  /**
   * Gets the status of a C-MOVE job
   */
  getJobStatus(jobId: string): Observable<MoveJobStatus> {
    return this.http.get<MoveJobStatus>(`${this.baseUrl}/move/jobs/${jobId}`);
  }

  /**
   * Records a successful C-MOVE send — creates a study_shares document with
   * business-safe expiration computed by the Cloud Function.
   * Called after each successful C-MOVE job.
   */
  recordStudySend(data: {
    studyInstanceUID: string;
    spokeFacilityId: string;
    hubFacilityId: string;
    caseName: string;
    sharedByUserId: string;
    patientName?: string;
    patientId?: string;
    studyDate?: string;
    accessionNumber?: string;
    numSeries?: number;
    numInstances?: number;
  }): Promise<{ success: boolean; shareId: string; deleteAt: string }> {
    const fn = firebase.functions().httpsCallable('studyLifetime-recordStudySend');
    return fn(data).then(result => result.data);
  }

  /**
   * Deletes a study from the user's facility Orthanc instance
   */
  deleteStudy(studyInstanceUid: string): Observable<{ success: boolean; studyInstanceUid: string; orthancId: string }> {
    return this.http.delete<{ success: boolean; studyInstanceUid: string; orthancId: string }>(
      `${this.baseUrl}/studies/${encodeURIComponent(studyInstanceUid)}`
    );
  }
}


